import { SimpleGrid, Stack, Text, Title } from "@mantine/core";
import {
  IconActivity,
  IconCircleCheck,
  IconClipboardList,
  IconClock,
  IconTruckDelivery,
} from "@tabler/icons-react";

import { useAuthContext } from "/@/shared/contexts/AuthContext";
import { UserType } from "/@/generated/graphql";
import { DraftResumeCard } from "./DraftResumeCard";
import { OrdersTable } from "./OrdersTable";
import { StatCard } from "./StatCard";
import { useDashboardData } from "./useDashboardData";

const formatToday = () => {
  const today = new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date());

  return today.charAt(0).toUpperCase() + today.slice(1);
};

export const DashboardV2 = () => {
  const { user } = useAuthContext();
  const { deliveredFrom, lastDraft, upcomingDeliveries, recentOrders, counts } =
    useDashboardData();
  const civility =
    user?.type === UserType.Dentist && !user?.managedUserGroup ? "Dr. " : "";

  return (
    <Stack gap="lg" p="md">
      <Stack gap={2}>
        <Title variant="display" size="lg">
          Bonjour,{" "}
          <Text span inherit variant="accent">
            {civility}
            {user?.firstName} {user?.lastName}
          </Text>
        </Title>
        <Text variant="caption">
          {formatToday()} ·{" "}
          {counts.drafts > 0 && (
            <>
              <Text span fw={600} inherit>
                {counts.drafts} brouillon{counts.drafts > 1 ? "s" : ""}
              </Text>{" "}
              à reprendre ·{" "}
            </>
          )}
          <Text span fw={600} inherit>
            {counts.inProgress}
          </Text>{" "}
          commande{counts.inProgress > 1 ? "s" : ""} en cours
        </Text>
      </Stack>

      {lastDraft && (
        <DraftResumeCard
          draft={lastDraft}
          otherDraftsCount={Math.max(counts.drafts - 1, 0)}
        />
      )}

      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="s4">
        <StatCard
          label="En cours"
          icon={<IconActivity size={15} />}
          value={counts.inProgress}
          hint="toutes commandes hors brouillons et livrées"
        />
        <StatCard
          label="À valider"
          icon={<IconClock size={15} />}
          value={counts.waitingForAnswer}
          hint="en attente de votre réponse"
          isWarning={counts.waitingForAnswer > 0}
        />
        <StatCard
          label="Livrées ce mois"
          icon={<IconCircleCheck size={15} />}
          value={counts.deliveredThisMonth}
          hint={new Date(deliveredFrom).toLocaleDateString("fr-FR", {
            month: "long",
          })}
        />
      </SimpleGrid>

      <OrdersTable
        title="Livraisons à venir"
        icon={<IconTruckDelivery size={24} />}
        hint={
          upcomingDeliveries.orders.length
            ? `${upcomingDeliveries.orders.length} prochaines`
            : undefined
        }
        orders={upcomingDeliveries.orders}
        materialAndShadeCatalog={upcomingDeliveries.materialAndShadeCatalog}
        emptyLabel="Aucune livraison prévue"
      />

      <OrdersTable
        title="Dernières commandes"
        icon={<IconClipboardList size={24} />}
        hint={
          counts.total
            ? `${recentOrders.orders.length} sur ${counts.total}`
            : undefined
        }
        orders={recentOrders.orders}
        materialAndShadeCatalog={recentOrders.materialAndShadeCatalog}
        emptyLabel="Aucune commande pour le moment"
      />
    </Stack>
  );
};
